/**
 * AzleenTVPlayer — Core player class.
 */

import { Favorites }  from './favorites';
import { HLSPlayer }  from './hls-player';

export class AzleenTVPlayer {
  constructor( el ) {
    this.root   = el;
    this.uid    = el.id;
    this.config = JSON.parse( el.dataset.config || '{}' );

    this.state = {
      page: 1, search: '', group: this.config.group || '',
      currentId: null, currentIndex: -1, activeTab: 'channels', searchTimer: null,
    };

    // Flat ordered list of channels currently loaded (for prev/next)
    this._channelOrder = [];

    this.favorites = new Favorites( this.uid );
    this.hlsPlayer = null;

    this._bindElements();
    this._bindEvents();
    this._loadChannels();
    this._renderFavorites();
    this._updateFavCount();
  }

  // ── Element references ────────────────────────────────────────────────────

  _bindElements() {
    const q  = ( sel ) => this.root.querySelector( sel );
    const id = ( s )   => q( `#${this.uid}-${s}` );

    this.els = {
      search:      q( '.azleentv-search' ),
      searchWrap:  q( '.azleentv-search-wrap' ),
      searchClear: q( '.azleentv-search-clear' ),
      groupSel:    q( '.azleentv-group-select' ),
      channelList: id( 'channel-list' ),
      favList:     id( 'favorites-list' ),
      pagination:  id( 'pagination' ),
      tabs:        this.root.querySelectorAll( '.azleentv-tab' ),
      tabPanels:   this.root.querySelectorAll( '.azleentv-tab-panel' ),
      video:       id( 'video' ),
      splash:      id( 'splash' ),
      nowPlaying:  id( 'now-playing' ),
      nowLogo:     id( 'now-logo' ),
      nowName:     id( 'now-name' ),
      nowGroup:    id( 'now-group' ),
      btnFav:      id( 'btn-fav' ),
      btnPrev:     id( 'btn-prev' ),
      btnNext:     id( 'btn-next' ),
      btnFull:     id( 'btn-fullscreen' ),
      errorBar:    id( 'error' ),
      errorMsg:    id( 'error-msg' ),
      favCount:    this.root.querySelector( '.azleentv-fav-count' ),
      // Slider / grid panel
      panelToggle: id( 'panel-toggle' ),
      channelPanel:id( 'channel-panel' ),
      sliderNav:   id( 'slider-nav' ),
      slidePrev:   id( 'slide-prev' ),
      slideNext:   id( 'slide-next' ),
      chCount:     id( 'ch-count' ),
    };
  }

  // ── Event binding ─────────────────────────────────────────────────────────

  _bindEvents() {
    const { search, searchClear, groupSel, btnFav, btnPrev, btnNext, btnFull, tabs } = this.els;

    if ( search ) {
      search.addEventListener( 'input', () => {
        clearTimeout( this.state.searchTimer );
        this.state.searchTimer = setTimeout( () => {
          this.state.search = search.value.trim();
          this.state.page   = 1;
          this._loadChannels();
          this.els.searchWrap && this.els.searchWrap.classList.toggle( 'has-value', !! this.state.search );
        }, 300 );
      } );
    }

    if ( searchClear ) {
      searchClear.addEventListener( 'click', () => {
        if ( search ) search.value = '';
        this.state.search = '';
        this.state.page   = 1;
        this._loadChannels();
        this.els.searchWrap && this.els.searchWrap.classList.remove( 'has-value' );
      } );
    }

    if ( groupSel ) {
      groupSel.addEventListener( 'change', () => {
        this.state.group = groupSel.value;
        this.state.page  = 1;
        this._loadChannels();
      } );
    }

    tabs.forEach( ( tab ) => tab.addEventListener( 'click', () => this._switchTab( tab.dataset.tab ) ) );

    if ( btnFav ) {
      btnFav.addEventListener( 'click', () => {
        if ( this.state.currentId === null ) return;
        const isFav = this.favorites.toggle( this.state.currentId );
        btnFav.classList.toggle( 'is-active', isFav );
        this._renderFavorites();
        this._updateFavCount();
      } );
    }

    if ( btnPrev ) {
      btnPrev.addEventListener( 'click', () => this._navigateChannel( -1 ) );
    }

    if ( btnNext ) {
      btnNext.addEventListener( 'click', () => this._navigateChannel( 1 ) );
    }

    if ( btnFull ) {
      btnFull.addEventListener( 'click', () => {
        const area = this.root.querySelector( `#${this.uid}-player-area` );
        if ( ! area ) return;
        document.fullscreenElement ? document.exitFullscreen() : area.requestFullscreen && area.requestFullscreen();
      } );
    }

    // Keyboard: left/right arrow for prev/next when player is focused
    this.root.addEventListener( 'keydown', ( e ) => {
      if ( e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' ) return;
      if ( e.key === 'ArrowLeft' )  this._navigateChannel( -1 );
      if ( e.key === 'ArrowRight' ) this._navigateChannel( 1 );
    } );

    // Panel toggle: slider ↔ grid
    const { panelToggle, channelPanel, slidePrev, slideNext, channelList } = this.els;

    if ( panelToggle && channelPanel ) {
      panelToggle.addEventListener( 'click', () => {
        const isGrid = channelPanel.classList.contains( 'is-grid' );
        channelPanel.classList.toggle( 'is-slider', isGrid );
        channelPanel.classList.toggle( 'is-grid', ! isGrid );
        panelToggle.setAttribute( 'aria-expanded', ! isGrid ? 'true' : 'false' );
        panelToggle.setAttribute( 'aria-label',
          ! isGrid
            ? ( window.azleentvData.i18n?.collapseChannels || 'Collapse channel list' )
            : ( window.azleentvData.i18n?.expandChannels   || 'Expand channel list' )
        );
        // Show/hide slider nav arrows
        if ( this.els.sliderNav ) {
          this.els.sliderNav.style.display = isGrid ? 'flex' : 'none';
        }
      } );
    }

    // Slider left/right scroll buttons
    if ( slidePrev && channelList ) {
      slidePrev.addEventListener( 'click', () => {
        channelList.scrollBy( { left: -280, behavior: 'smooth' } );
      } );
    }
    if ( slideNext && channelList ) {
      slideNext.addEventListener( 'click', () => {
        channelList.scrollBy( { left: 280, behavior: 'smooth' } );
      } );
    }
  }

  // ── Tab switching ──────────────────────────────────────────────────────────

  _switchTab( tabName ) {
    this.state.activeTab = tabName;
    this.els.tabs.forEach( ( t ) => {
      const on = t.dataset.tab === tabName;
      t.classList.toggle( 'azleentv-tab--active', on );
      t.setAttribute( 'aria-selected', on ? 'true' : 'false' );
    } );
    this.els.tabPanels.forEach( ( p ) => {
      const on = p.id === `${this.uid}-tab-${tabName}`;
      p.classList.toggle( 'azleentv-tab-panel--active', on );
      p.hidden = ! on;
    } );
  }

  // ── Channel loading ────────────────────────────────────────────────────────

  async _loadChannels() {
    const list = this.els.channelList;
    if ( ! list ) return;
    this._showSkeleton( list );

    const body = new FormData();
    body.append( 'action',   'azleentv_get_channels' );
    body.append( 'nonce',    window.azleentvData.nonce );
    body.append( 'name',     this.state.search );
    body.append( 'group',    this.state.group );
    body.append( 'page',     this.state.page );
    body.append( 'per_page', this.config.perPage || 24 );

    if ( this.config.channelIds && this.config.channelIds.length ) {
      body.append( 'ids', this.config.channelIds.join( ',' ) );
    }

    try {
      const res  = await fetch( window.azleentvData.ajaxUrl, { method: 'POST', body } );
      const json = await res.json();
      if ( json.success ) {
        this._channelOrder = json.data.channels;
        this._renderChannels( list, json.data.channels );
        this._renderPagination( json.data );
        this._updateNavButtons();
        // Update count badge
        if ( this.els.chCount ) {
          this.els.chCount.textContent = json.data.total
            ? json.data.total.toLocaleString()
            : '';
        }
        // Show slider nav arrows when in slider mode
        const isSlider = this.els.channelPanel && this.els.channelPanel.classList.contains( 'is-slider' );
        if ( this.els.sliderNav ) {
          this.els.sliderNav.style.display = isSlider ? 'flex' : 'none';
        }
      } else {
        this._showError( list, 'Failed to load channels.' );
      }
    } catch {
      this._showError( list, 'Network error. Please try again.' );
    }
  }

  _showSkeleton( list ) {
    list.innerHTML = `<div class="azleentv-skeleton-list" aria-hidden="true">
      ${[ ...Array( 8 ) ].map( () => `
        <div class="azleentv-skeleton-item">
          <div class="azleentv-skeleton azleentv-skeleton--logo"></div>
          <div class="azleentv-skeleton-text">
            <div class="azleentv-skeleton azleentv-skeleton--name"></div>
            <div class="azleentv-skeleton azleentv-skeleton--group"></div>
          </div>
        </div>` ).join( '' )}
    </div>`;
  }

  _showError( list, msg ) {
    list.innerHTML = `<div class="azleentv-empty-state">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      <p>${this._esc( msg )}</p>
    </div>`;
  }

  _renderChannels( list, channels ) {
    if ( ! channels || ! channels.length ) {
      list.innerHTML = `<div class="azleentv-empty-state">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <rect x="2" y="7" width="20" height="15" rx="2"/><path d="M17 2l-5 5-5-5"/>
        </svg>
        <p>No channels found</p>
        <p class="azleentv-empty-sub">Try a different search or country.</p>
      </div>`;
      return;
    }

    list.innerHTML = channels.map( ( ch ) => this._channelItemHTML( ch ) ).join( '' );

    list.querySelectorAll( '.azleentv-channel-item' ).forEach( ( item ) => {
      item.addEventListener( 'click', () => this._playFromItem( item ) );
      item.addEventListener( 'keydown', ( e ) => {
        if ( e.key === 'Enter' || e.key === ' ' ) { e.preventDefault(); this._playFromItem( item ); }
      } );

      const favBtn = item.querySelector( '.azleentv-channel-fav' );
      if ( favBtn ) {
        favBtn.addEventListener( 'click', ( e ) => {
          e.stopPropagation();
          const id    = parseInt( item.dataset.id, 10 );
          const isFav = this.favorites.toggle( id );
          favBtn.classList.toggle( 'azleentv-channel-fav--active', isFav );
          if ( this.state.currentId === id && this.els.btnFav ) {
            this.els.btnFav.classList.toggle( 'is-active', isFav );
          }
          this._renderFavorites();
          this._updateFavCount();
        } );
      }
    } );

    // Restore active highlight
    if ( this.state.currentId !== null ) {
      const active = list.querySelector( `[data-id="${this.state.currentId}"]` );
      if ( active ) active.classList.add( 'azleentv-channel-item--active' );
    }
  }

  _playFromItem( item ) {
    const id    = parseInt( item.dataset.id, 10 );
    const token = item.dataset.token || '';
    // Track position in current order for prev/next
    this.state.currentIndex = this._channelOrder.findIndex( ( ch ) => Number( ch.id ) === id );
    this._playChannel( id, token, item );
  }

  _channelItemHTML( ch ) {
    const id     = Number.isInteger( ch.id ) ? ch.id : '';
    const name   = this._esc( ch.name );
    const group  = this._esc( ch.group );
    const logo   = this._esc( ch.logo );
    // TASK-008: use streamToken instead of raw url
    const token  = this._esc( ch.streamToken || '' );
    const letter = ch.name ? ch.name.charAt( 0 ).toUpperCase() : '?';
    const isFav  = this.favorites.has( id );
    const star   = isFav ? 'azleentv-channel-fav--active' : '';

    const logoHTML = logo
      ? `<img class="azleentv-channel-logo" src="${logo}" alt="" loading="lazy" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'"/>
         <div class="azleentv-channel-logo-fallback" style="display:none">${letter}</div>`
      : `<div class="azleentv-channel-logo-fallback">${letter}</div>`;

    return `<div class="azleentv-channel-item" data-id="${id}" data-token="${token}" role="button" tabindex="0" aria-label="Watch ${name}">
      <div class="azleentv-channel-logo-wrap">${logoHTML}</div>
      <div class="azleentv-channel-info">
        <span class="azleentv-channel-name">${name}</span>
        <span class="azleentv-channel-group">${group}</span>
      </div>
      <button class="azleentv-channel-fav ${star}" aria-label="${isFav ? 'Remove from favourites' : 'Add to favourites'}">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="${isFav ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
      </button>
    </div>`;
  }

  // ── Pagination ─────────────────────────────────────────────────────────────

  _renderPagination( data ) {
    const pag = this.els.pagination;
    if ( ! pag ) return;
    const { page, pages } = data;
    if ( pages <= 1 ) { pag.innerHTML = ''; return; }

    const range = this._pageRange( page, pages );
    let html = `<button class="azleentv-page-btn" data-page="${page - 1}" ${page <= 1 ? 'disabled' : ''} aria-label="Previous page">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>
    </button>`;

    let last = 0;
    range.forEach( ( p ) => {
      if ( p - last > 1 ) html += `<span class="azleentv-page-btn" style="cursor:default;opacity:.4">…</span>`;
      html += `<button class="azleentv-page-btn ${p === page ? 'azleentv-page-btn--active' : ''}" data-page="${p}" aria-current="${p === page ? 'page' : 'false'}">${p}</button>`;
      last = p;
    } );

    html += `<button class="azleentv-page-btn" data-page="${page + 1}" ${page >= pages ? 'disabled' : ''} aria-label="Next page">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
    </button>`;

    pag.innerHTML = html;
    pag.querySelectorAll( '.azleentv-page-btn[data-page]' ).forEach( ( btn ) => {
      if ( btn.disabled ) return;
      btn.addEventListener( 'click', () => {
        const p = parseInt( btn.dataset.page, 10 );
        if ( p < 1 || p > pages ) return;
        this.state.page = p;
        this._loadChannels();
        if ( this.els.channelList ) this.els.channelList.scrollTop = 0;
      } );
    } );
  }

  _pageRange( cur, total ) {
    const d = 2, range = [];
    for ( let i = Math.max( 1, cur - d ); i <= Math.min( total, cur + d ); i++ ) range.push( i );
    if ( range[0] > 1 ) range.unshift( 1 );
    if ( range[ range.length - 1 ] < total ) range.push( total );
    return range;
  }

  // ── Prev / Next navigation ─────────────────────────────────────────────────

  _navigateChannel( direction ) {
    if ( ! this._channelOrder.length ) return;

    let idx = this.state.currentIndex + direction;
    if ( idx < 0 ) idx = this._channelOrder.length - 1;
    if ( idx >= this._channelOrder.length ) idx = 0;

    const ch = this._channelOrder[ idx ];
    if ( ! ch ) return;

    this.state.currentIndex = idx;

    const item = this.els.channelList
      ? this.els.channelList.querySelector( `[data-id="${ch.id}"]` )
      : null;

    // TASK-008: use streamToken from channel order
    this._playChannel( Number( ch.id ), ch.streamToken || '', item );

    if ( item ) item.scrollIntoView( { block: 'nearest', behavior: 'smooth' } );
  }

  _updateNavButtons() {
    const hasList = this._channelOrder.length > 1;
    if ( this.els.btnPrev ) this.els.btnPrev.disabled = ! hasList;
    if ( this.els.btnNext ) this.els.btnNext.disabled = ! hasList;
  }

  // ── Playback ───────────────────────────────────────────────────────────────

  /**
   * TASK-008 : Play a channel using the stream proxy URL derived from its token.
   * On token_expired, fetches a fresh token once and retries.
   *
   * @param {number}      id     Channel DB ID.
   * @param {string}      token  streamToken from AJAX response.
   * @param {HTMLElement} itemEl The clicked channel list item (may be null).
   */
  _playChannel( id, token, itemEl ) {
    this.root.querySelectorAll( '.azleentv-channel-item--active' )
      .forEach( ( el ) => el.classList.remove( 'azleentv-channel-item--active' ) );
    if ( itemEl ) itemEl.classList.add( 'azleentv-channel-item--active' );

    this.state.currentId = id;

    if ( this.els.splash )   this.els.splash.classList.add( 'is-hidden' );
    if ( this.els.errorBar ) this.els.errorBar.hidden = true;

    if ( this.els.nowPlaying ) {
      this.els.nowPlaying.hidden = false;

      const name  = itemEl ? itemEl.querySelector( '.azleentv-channel-name' )?.textContent  || '' : '';
      const group = itemEl ? itemEl.querySelector( '.azleentv-channel-group' )?.textContent || '' : '';

      if ( this.els.nowName )  this.els.nowName.textContent  = name;
      if ( this.els.nowGroup ) this.els.nowGroup.textContent = group;

      const logoImg = itemEl ? itemEl.querySelector( '.azleentv-channel-logo' ) : null;
      if ( this.els.nowLogo ) {
        this.els.nowLogo.src     = logoImg ? logoImg.src : '';
        this.els.nowLogo.alt     = name;
        this.els.nowLogo.onerror = () => { this.els.nowLogo.style.display = 'none'; };
        this.els.nowLogo.onload  = () => { this.els.nowLogo.style.display = ''; };
      }

      if ( this.els.btnFav ) {
        this.els.btnFav.classList.toggle( 'is-active', this.favorites.has( id ) );
      }
    }

    if ( this.hlsPlayer ) this.hlsPlayer.destroy();

    // TASK-008 : Build proxy URL. Fall back to empty string if no token (key missing).
    const proxyUrl = token ? HLSPlayer.proxyUrl( id, token ) : '';

    if ( ! proxyUrl ) {
      this._showStreamError( 'Stream unavailable — encryption key not configured.' );
      return;
    }

    this.hlsPlayer = new HLSPlayer( this.els.video, {
      onError: ( msg ) => this._showStreamError( msg ),
      // TASK-008 : On token expiry, fetch a fresh token once and retry.
      onTokenExpired: () => this._refreshTokenAndPlay( id, itemEl ),
    } );
    this.hlsPlayer.load( proxyUrl );
  }

  /**
   * TASK-008 : Fetch a fresh streamToken for channel `id` and retry playback once.
   *
   * @param {number}      id     Channel DB ID.
   * @param {HTMLElement} itemEl The channel list item (may be null).
   */
  async _refreshTokenAndPlay( id, itemEl ) {
    try {
      const body = new FormData();
      body.append( 'action', 'azleentv_get_channels' );
      body.append( 'nonce',  window.azleentvData.nonce );
      body.append( 'ids',    id );

      const res  = await fetch( window.azleentvData.ajaxUrl, { method: 'POST', body } );
      const json = await res.json();

      if ( json.success && json.data.channels && json.data.channels.length ) {
        const fresh = json.data.channels[0];
        if ( fresh.streamToken ) {
          if ( this.hlsPlayer ) this.hlsPlayer.destroy();
          this.hlsPlayer = new HLSPlayer( this.els.video, {
            onError: ( msg ) => this._showStreamError( msg ),
          } );
          this.hlsPlayer.load( HLSPlayer.proxyUrl( id, fresh.streamToken ) );
          // Update the DOM item's token for future navigations.
          if ( itemEl ) itemEl.dataset.token = fresh.streamToken;
          return;
        }
      }
    } catch ( _err ) { /* fall through */ }

    this._showStreamError( 'Stream token expired. Please try again.' );
  }

  _showStreamError( msg ) {
    if ( this.els.errorBar ) {
      this.els.errorBar.hidden = false;
      if ( this.els.errorMsg ) this.els.errorMsg.textContent = msg || 'Stream unavailable. Please try another channel.';
    }
  }

  // ── Favourites ─────────────────────────────────────────────────────────────

  _renderFavorites() {
    const list = this.els.favList;
    if ( ! list ) return;
    const ids = this.favorites.getAll();

    if ( ! ids.length ) {
      list.innerHTML = `<div class="azleentv-empty-state">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
        </svg>
        <p>No favourites yet</p>
        <p class="azleentv-empty-sub">Tap the star on any channel to save it here.</p>
      </div>`;
      return;
    }

    const body = new FormData();
    body.append( 'action', 'azleentv_get_channels' );
    body.append( 'nonce',  window.azleentvData.nonce );
    body.append( 'ids',    ids.join( ',' ) );

    fetch( window.azleentvData.ajaxUrl, { method: 'POST', body } )
      .then( ( r ) => r.json() )
      .then( ( json ) => {
        if ( ! json.success ) return;
        list.innerHTML = json.data.channels.map( ( ch ) => this._channelItemHTML( ch ) ).join( '' );
        list.querySelectorAll( '.azleentv-channel-item' ).forEach( ( item ) => {
          item.addEventListener( 'click', () => this._playFromItem( item ) );
        } );
      } );
  }

  _updateFavCount() {
    const el = this.els.favCount;
    if ( ! el ) return;
    const c = this.favorites.count();
    el.textContent = c > 0 ? c : '';
    el.setAttribute( 'data-count', c );
  }

  // ── Utility ────────────────────────────────────────────────────────────────

  _esc( str ) {
    return String( str )
      .replace( /&/g, '&amp;' ).replace( /</g, '&lt;' )
      .replace( />/g, '&gt;' ).replace( /"/g, '&quot;' )
      .replace( /'/g, '&#039;' );
  }
}
